import React, { useEffect, useMemo } from "react";
import { Stack, Box, InputAdornment } from "@mui/material";
import { Formik, Form, FormikProps } from "formik";
import * as Yup from "yup";
import AddBoxIcon from "@mui/icons-material/AddBox";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";

import FormAlert from "../components/common/FormAlert";
import FormTextField from "../components/common/FormTextField";
import FormSelectField from "../components/common/FormSelectField";
import SubmitButton from "../components/buttons/SubmitButton";

import { useAppDispatch, useAppSelector } from "../redux/store";
import {
  createInventoryItem,
  updateInventoryItem,
} from "../redux/actions/inventoryItemActions";
import { getSuppliers } from "../redux/actions/supplierActions";
import { inventoryItemActions } from "../redux/slices/inventoryItem";
import { cleanFormData } from "../helpers/formHelpers";

import { InventoryItemResponseDto } from "../shared/dtos/inventoryItem/InventoryItemResponseDto";
import { InventoryItemRequestDto } from "../shared/dtos/inventoryItem/InventoryItemRequestDto";

const inventorySchema = Yup.object().shape({
  itemCode: Yup.string().required("Item Code is required"),
  name: Yup.string().required("Item Name is required"),
  description: Yup.string().nullable(),
  category: Yup.string().required("Category is required"),
  quantityOnHand: Yup.number()
    .required("Quantity is required")
    .min(0, "Quantity cannot be negative"),
  reorderLevel: Yup.number()
    .required("Reorder Level is required")
    .min(0, "Reorder level cannot be negative"),
  unitOfMeasure: Yup.string().required(
    "Unit of measure (e.g. Box, kg) is required",
  ),
  unitCost: Yup.number()
    .required("Unit Cost is required")
    .min(0, "Unit cost cannot be negative"),
  preferredSupplierId: Yup.string().nullable(),
});

interface CreateOrUpdateInventoryItemFormProps {
  inventory?: InventoryItemResponseDto | null;
  isEditing?: boolean;
  loading?: boolean;
  error?: string | null;
}

const CreateOrUpdateInventoryItemForm: React.FC<
  CreateOrUpdateInventoryItemFormProps
> = ({
  inventory = null,
  isEditing = false,
  loading = false,
  error = null,
}) => {
  const dispatch = useAppDispatch();
  const { items: suppliers } = useAppSelector((state) => state.supplier);

  useEffect(() => {
    if (!suppliers || suppliers.length === 0) {
      dispatch(getSuppliers());
    }
  }, [dispatch, suppliers]);

  const supplierOptions = useMemo(() => {
    const options =
      suppliers?.map((s) => ({ value: s.id, label: s.name })) || [];
    return [{ value: "", label: "None Assigned" }, ...options];
  }, [suppliers]);

  const initialValues: InventoryItemRequestDto = {
    itemCode: inventory?.itemCode || "",
    name: inventory?.name || "",
    description: inventory?.description || "",
    category: inventory?.category || "",
    quantityOnHand: 0,
    reorderLevel: inventory?.reorderLevel ?? 10,
    unitOfMeasure: inventory?.unitOfMeasure || "",
    unitCost: inventory?.unitCost ?? 0,
    preferredSupplierId: inventory?.preferredSupplierId || "",
  };

  return (
    <Stack spacing={2} sx={{ width: "100%", textAlign: "center" }}>
      <FormAlert
        message={error}
        severity="error"
        closable
        onClose={
          isEditing
            ? () => dispatch(inventoryItemActions.setErrorUpdate(null))
            : () => dispatch(inventoryItemActions.setErrorCreate(null))
        }
      />

      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={inventorySchema}
        onSubmit={(values) => {
          const rawCleaned = cleanFormData(values) as Record<string, any>;

          const cleanedValues: InventoryItemRequestDto = {
            itemCode: rawCleaned.itemCode,
            name: rawCleaned.name,
            description: rawCleaned.description || null,
            category: rawCleaned.category || null,
            quantityOnHand: Number(rawCleaned.quantityOnHand),
            reorderLevel: Number(rawCleaned.reorderLevel),
            unitOfMeasure: rawCleaned.unitOfMeasure,
            unitCost: Number(rawCleaned.unitCost),
            preferredSupplierId: rawCleaned.preferredSupplierId || null,
          };

          if (isEditing && inventory) {
            dispatch(
              updateInventoryItem({ id: inventory.id, values: cleanedValues }),
            );
          } else {
            dispatch(createInventoryItem(cleanedValues));
          }
        }}
      >
        {({ dirty, isValid }: FormikProps<InventoryItemRequestDto>) => (
          <Form>
            <Stack spacing={2}>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 2fr" },
                  gap: 2,
                }}
              >
                <FormTextField
                  name="itemCode"
                  label="Item Code"
                  placeholder="e.g. PEN-001"
                />
                <FormTextField
                  name="name"
                  label="Item Name"
                  placeholder="e.g. Blue Ballpoint Pen"
                />
              </Box>

              <FormTextField
                name="description"
                label="Description"
                multiline
                rows={2}
              />

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  gap: 2,
                }}
              >
                <FormTextField
                  name="category"
                  label="Category"
                  placeholder="e.g. Stationery"
                />
                <FormSelectField
                  name="preferredSupplierId"
                  label="Preferred Supplier"
                  options={supplierOptions}
                />
              </Box>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "repeat(4, 1fr)" },
                  gap: 2,
                }}
              >
                {!isEditing && (
                  <FormTextField
                    name="quantityOnHand"
                    label="Quantity On Hand"
                    type="number"
                    slotProps={{ htmlInput: { min: 0 } }}
                  />
                )}

                <FormTextField
                  name="reorderLevel"
                  label="Reorder Level"
                  type="number"
                  helperText="Alert limit"
                  slotProps={{ htmlInput: { min: 0 } }}
                />
                <FormTextField
                  name="unitOfMeasure"
                  label="Unit (UoM)"
                  placeholder="e.g. Box, Pcs"
                />
                <FormTextField
                  name="unitCost"
                  label="Unit Cost"
                  type="number"
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">KES</InputAdornment>
                      ),
                    },
                    htmlInput: { min: 0 },
                  }}
                />
              </Box>

              <SubmitButton
                title={isEditing ? "Save Changes" : "Create Item"}
                loadingText={isEditing ? "Saving..." : "Creating..."}
                loading={loading}
                disabled={!dirty || !isValid || loading}
                startIcon={isEditing ? <SaveOutlinedIcon /> : <AddBoxIcon />}
                sx={{ mt: 1 }}
              />
            </Stack>
          </Form>
        )}
      </Formik>
    </Stack>
  );
};

export default CreateOrUpdateInventoryItemForm;
